import { Injectable, NgZone } from "@angular/core";
import { MatSnackBar, MatSnackBarConfig } from "@angular/material/snack-bar";

@Injectable({
  providedIn: "root",
})
export class NotificationService {
  private defaultDuration = 3000; // Default duration in milliseconds
  private defaultHorizontalPosition: MatSnackBarConfig["horizontalPosition"] =
    "center";
  private defaultVerticalPosition: MatSnackBarConfig["verticalPosition"] =
    "bottom";

  constructor(
    private snackBar: MatSnackBar,
    private zone: NgZone,
  ) {}

  private show(
    message: string,
    panelClass: string | string[],
    config?: MatSnackBarConfig,
  ): void {
    this.zone.run(() => {
      this.snackBar.open(message, "Dismiss", {
        duration: config?.duration || this.defaultDuration,
        horizontalPosition:
          config?.horizontalPosition || this.defaultHorizontalPosition,
        verticalPosition:
          config?.verticalPosition || this.defaultVerticalPosition,
        panelClass: panelClass,
        ...config, // Override with any custom configurations
      });
    });
  }

  /**
   * Shows a success notification.
   * @param message The message to display.
   * @param config Optional configuration for the snack bar.
   *
   * Example:
   * ```typescript
   * this.notificationService.success('Operation completed successfully!');
   * ```
   */
  success(message: string, config?: MatSnackBarConfig): void {
    this.show(message, ["success-snackbar"], config);
  }

  /**
   * Shows an error notification.
   * @param message The message to display.
   * @param config Optional configuration for the snack bar.
   *
   * Example:
   * ```typescript
   * this.notificationService.error('An error occurred!');
   * ```
   */
  error(message: string, config?: MatSnackBarConfig): void {
    this.show(message, ["error-snackbar"], config);
  }

  /**
   * Shows a warning notification.
   * @param message The message to display.
   * @param config Optional configuration for the snack bar.
   *
   * Example:
   * ```typescript
   * this.notificationService.warn('Be careful!');
   * ```
   */
  warn(message: string, config?: MatSnackBarConfig): void {
    this.show(message, ["warn-snackbar"], config);
  }

  /**
   * Shows an information notification.
   * @param message The message to display.
   * @param config Optional configuration for the snack bar.
   *
   * Example:
   * ```typescript
   * this.notificationService.info('Please check your email.');
   * ```
   */
  info(message: string, config?: MatSnackBarConfig): void {
    this.show(message, ["info-snackbar"], config);
  }

  /**
   * Shows a loading notification.
   * @param message The message to display.
   * @param config Optional configuration for the snack bar.
   *
   * Example:
   * ```typescript
   * this.notificationService.loading('Loading data...');
   * ```
   */
  loading(message: string, config?: MatSnackBarConfig): void {
    this.show(message, ["loading-snackbar"], config);
  }

  /**
   * Shows a custom notification.
   * @param message The message to display.
   * @param panelClass The CSS class(es) to apply to the snack bar.
   * @param config Optional configuration for the snack bar.
   *
   * Example:
   * ```typescript
   * this.notificationService.custom('Custom message', 'my-custom-class', { duration: 5000 });
   * ```
   */
  custom(
    message: string,
    panelClass: string | string[],
    config?: MatSnackBarConfig,
  ): void {
    this.show(message, panelClass, config);
  }

  /**
   * Sets the default duration for all notifications.
   * @param duration The duration in milliseconds.
   *
   * Example:
   * ```typescript
   * this.notificationService.setDefaultDuration(5000);
   * ```
   */
  setDefaultDuration(duration: number): void {
    this.defaultDuration = duration;
  }

  /**
   * Sets the default horizontal position for all notifications.
   * @param position The horizontal position.
   *
   * Example:
   * ```typescript
   * this.notificationService.setDefaultHorizontalPosition('left');
   * ```
   */
  setDefaultHorizontalPosition(
    position: MatSnackBarConfig["horizontalPosition"],
  ): void {
    this.defaultHorizontalPosition = position;
  }

  /**
   * Sets the default vertical position for all notifications.
   * @param position The vertical position.
   *
   * Example:
   * ```typescript
   * this.notificationService.setDefaultVerticalPosition('top');
   * ```
   */
  setDefaultVerticalPosition(
    position: MatSnackBarConfig["verticalPosition"],
  ): void {
    this.defaultVerticalPosition = position;
  }
}
